import { CONSTANTS } from "../../data";
import { calculateXp } from "../../utils/gameCalculations";
import type { GameState } from "../../types";

const getDecorationBonuses = (state: GameState) => {
  const effectsMap = ((CONSTANTS as any).DECORATION_EFFECTS || {}) as Record<string, any>;
  const ownedDecorations = ((state as any).decorations || {}) as Record<string, boolean>;

  let tipBonus = 0;
  let xpBonusPercent = 0;

  Object.keys(ownedDecorations).forEach((key) => {
    if (ownedDecorations[key]) {
      const e = effectsMap[key];
      if (e?.tipBonus) tipBonus += e.tipBonus;
      if (e?.xpBonusPercent) xpBonusPercent += e.xpBonusPercent;
    }
  });

  return { tipBonus, xpBonusPercent };
};

export const customerReducer = (state: GameState, action: any): GameState => {
  switch (action.type) {
    case "ADD_CUSTOMER":
      return {
        ...state,
        customers: [...state.customers, action.customer],
        customerIdCounter: state.customerIdCounter + 1,
      };

    case "REMOVE_CUSTOMER":
      return {
        ...state,
        customers: state.customers.filter(c => c.id !== action.customerId),
      };

    case "UPDATE_ALL_CUSTOMER_PATIENCE": {
      let reputationLoss = 0;
      const remainingCustomers = [];
      
      for (const customer of state.customers) {
        if (!customer.active) {
          remainingCustomers.push(customer);
          continue;
        }
        
        if (customer.type === 'tutorial') {
          remainingCustomers.push(customer);
          continue;
        }
    
        const newPatience = customer.patience - 1;
        if (newPatience > 0) {
          remainingCustomers.push({ ...customer, patience: newPatience });
        } else {
          // Customer leaves
          reputationLoss += CONSTANTS.REPUTATION_CONFIG.LOSSES.CUSTOMER_LEAVES;
        }
      }
      
      return {
        ...state,
        customers: remainingCustomers,
        reputation: Math.max(
          CONSTANTS.REPUTATION_CONFIG.MIN_REPUTATION, 
          state.reputation + reputationLoss
        ),
      };
    }

    case "SERVE_CUSTOMER": {
      const { customerId, itemId } = action;
      const customer = state.customers.find(c => c.id === customerId);
      if (!customer) return state;

      const allItemsMap = new Map();
      Object.entries(CONSTANTS.ITEMS).forEach(([id, item]) => allItemsMap.set(id, { ...item, id }));
      Object.entries(CONSTANTS.RECIPES).forEach(([id, recipe]) => allItemsMap.set(recipe.result.id, recipe.result));
      const item = allItemsMap.get(itemId);
      if (!item) return state;

      const serveInventory = { ...state.inventory };
      serveInventory[itemId] = Math.max(0, (serveInventory[itemId] || 0) - 1);
      
      let tip = item?.price || 0;
      let xp = calculateXp(item);
      let reputationGain = CONSTANTS.REPUTATION_CONFIG.GAINS.SERVE_REGULAR;
      if (CONSTANTS.PREMIUM_DRINKS.includes(itemId)) {
        reputationGain = CONSTANTS.REPUTATION_CONFIG.GAINS.SERVE_PREMIUM;
      }

      // Apply quality bonus
      const qualityMultipliers = state.qualityMultipliers[itemId];
      let newQualityMultipliers = state.qualityMultipliers;
      if (qualityMultipliers && qualityMultipliers.length > 0) {
        const multiplier = qualityMultipliers[0];
        tip = Math.round(tip * multiplier);
        const updatedMultipliers = qualityMultipliers.slice(1);
        newQualityMultipliers = { ...state.qualityMultipliers, [itemId]: updatedMultipliers };
      }

      // Apply preference bonus
      if (customer.preferenceTag && CONSTANTS.ITEM_TAGS[itemId]?.includes(customer.preferenceTag)) {
        tip = Math.round(tip * (1 + (customer.preferenceBonus || CONSTANTS.CUSTOMER_PREFERENCES.TIP_BONUS)));
      }

      // Apply trending bonus
      if (state.trendingDrink.id === itemId && Date.now() < state.trendingDrink.endTime) {
        tip = Math.round(tip * (1 + CONSTANTS.TREND_SYSTEM.TIP_BONUS));
      }

      // Apply rush mode tip bonus
      if (state.sessionMode.type === "rush") {
        tip = Math.round(tip * (1 + (CONSTANTS.RUSH_MODE as any).tipBonus));
      }

      // Apply decoration passive bonuses if any
      const { tipBonus: decoTipBonus, xpBonusPercent } = getDecorationBonuses(state);
      if (decoTipBonus > 0) {
        tip = Math.round(tip * (1 + decoTipBonus));
      }
      if (xpBonusPercent > 0) {
        xp = Math.max(1, Math.round(xp * (1 + xpBonusPercent)));
      }

      // Update rush mode score
      let newSessionMode = state.sessionMode;
      if (state.sessionMode.type === "rush") {
        const newScore = (state.sessionMode.score || 0) + tip * (CONSTANTS.RUSH_MODE as any).scoreMultipliers.tips;
        const newServed = (state.sessionMode.served || 0) + 1;
        const newTips = (state.sessionMode.tips || 0) + tip;
        newSessionMode = { ...state.sessionMode, score: newScore, served: newServed, tips: newTips };
      }
      
      // Update challenge progress if active
      let updatedChallenge = state.masterChefChallenge;
      if (state.masterChefChallenge.active) {
        const challenge = CONSTANTS.MASTER_CHEF_CHALLENGES[state.masterChefChallenge.id];
        if (challenge) {
          let progressIncrement = 0;
          if (challenge.trackingKey === 'perfectLatteArt' && action.latteArtPerfect) {
            progressIncrement++;
          }
          if (challenge.trackingKey === 'icedServed' && CONSTANTS.ITEM_TAGS[itemId]?.includes('iced')) {
            progressIncrement++;
          }
          if (progressIncrement > 0) {
            updatedChallenge = {
              ...state.masterChefChallenge,
              progress: state.masterChefChallenge.progress + progressIncrement
            };
          }
        }
      }
      
      return {
        ...state,
        inventory: serveInventory,
        customers: state.customers.filter(c => c.id !== customerId),
        coins: state.coins + tip,
        xp: state.xp + xp,
        reputation: Math.min(CONSTANTS.REPUTATION_CONFIG.MAX_REPUTATION, state.reputation + reputationGain),
        masterChefChallenge: updatedChallenge,
        qualityMultipliers: newQualityMultipliers,
        sessionMode: newSessionMode,
      };
    }

    case "TOGGLE_CUSTOMER_QUEUE_PANEL":
      return { ...state, customerQueueOpen: !state.customerQueueOpen };

    default:
      return state;
  }
};
